import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';
import { Country } from 'react-country-state-city/dist/esm/types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { TAdditionalUserDetails, TCustomerDetails, TEditCustomerDetailsReduxParams, TEditCustomerDetailsValidation } from '~types/feat/customer';

import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import { CustomerAccFundTypeEnum, CustomerAccTypeEnum, CustomerAccStatusEnum, CustomerGenderEnum, CustomerIdTypeEnum, CustomerRaceEnum, FundTypeEnum, BranchStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_customerManagementCustomerDetailsForm.module.scss';
import countryList from '~utils/country';
import combinedActions from '~redux/combinedActions';

type TCustomerManagementCustomerDetailsForm = {
    submitButtonLabel: string;
    onSubmit: (data: TEditCustomerDetailsReduxParams) => void;
    error: string;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
    initialCustomerDetails?: TCustomerDetails & { branchCode: string };
}

const CustomerManagementCustomerDetailsForm: FC<TCustomerManagementCustomerDetailsForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    isEditing,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
    initialCustomerDetails,
}) => {
    const { Misc: { dateTimeFormat, blockEnterKey, sanitizeReferralCode } } = UtilFunctions;

    const branches = useAppSelector(state => state.branch.allBranches);
    const { roleName } = useAppSelector(state => state.auth.userDetails);

    const isSuperAdmin = roleName === 'Super Admin';

    const [countries, setCountries] = useState<Country[]>([]);

    const CustomerManagementCustomerDetailsFormValidation = Yup.object().shape({
        name: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().validateCustomerName().label('Customer Name').required('Customer Name is required'),
        email: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().email().label('Email'),
        CIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersOnly().max(11).label('CIF Number').required('CIF Number is required'),
        idType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('ID Type').required('ID Type is required'),
        idNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace()
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum.IC];
                },
                then: (schema) => schema.length(12).label('IC number').matchNumbersOnly(),
            })
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum['Company Registration No']];
                },
                then: (schema) => schema.max(12).label('Company Registration Number').matches(/^[a-zA-Z0-9-]+$/g, { message: 'Invalid character detected' }),
            })
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum.Passport];
                },
                then: (schema) => schema.max(12).label('Passport number').matchNumbersAndAlphabets(),
            })
            .required('ID Number is required'),
        contactNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().max(17).matchNumbersOnly().label('Contact Number').required('Contact Number is required'),
        branchName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(branches.map(branch => branch.branchName), 'Invalid branch name').label('Branch Name').required('Branch Name is required'),
        branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(branches.map(branch => branch.branchCode), 'Invalid branch code').label('Branch Code').required('Branch Code is required'),
        // gender: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerGenderEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Gender').required('Gender is required'),
        nationality: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(countries.map(country => country.name), 'Invalid country').label('Nationality').required('Nationality is required'),
        occupation: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Occupation').required('Occupation is required'),
        addressLine1: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Address Line 1').required('Address Line 1 is required'),
        addressLine2: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Address Line 2'),
        city: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('City').required('City is required'),
        state: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('State').required('State is required'),
        postCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Postcode').required('Postcode is required'),
        race: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Race').required('Race is required'),
        // dateOfBirth: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Date of Birth').required('Date of Birth is required'),
        fundType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerAccFundTypeEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Fund Type').required('Fund Type is required'),
        refCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matches(/^[^=+\-@]*$/, { message: 'Cannot include these characters (=, +, -,@)' }).label('Referral Code'),
        status: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerAccStatusEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Status').required('Account status is required'),
    });

    const CustomerManagementJointAccountValidation = Yup.object().shape({
        additionalUserDetails: Yup.array().of(
            Yup.object().shape({
                childId: Yup.number().optional(),
                name: Yup.reach(CustomerManagementCustomerDetailsFormValidation, 'name'),
                idType: Yup.reach(CustomerManagementCustomerDetailsFormValidation, 'idType'),
                idNo: Yup.reach(CustomerManagementCustomerDetailsFormValidation, 'idNo'),
                nationality: Yup.reach(CustomerManagementCustomerDetailsFormValidation, 'nationality'),
                race: Yup.reach(CustomerManagementCustomerDetailsFormValidation, 'race'),
            })
        ),
    });

    const getUserBranchName = (id: number | undefined) => {

        if (!id) return '';

        const userBranch = branches.find(branch => branch.branchId === initialCustomerDetails?.branchId);

        return userBranch?.branchName;
    };

    const CustomerManagementCustomerDetailsFormInitialValues: TEditCustomerDetailsValidation = {
        name: initialCustomerDetails?.name || '',
        email: initialCustomerDetails?.email || '',
        CIFNo: initialCustomerDetails?.CIFNo || '',
        idType: initialCustomerDetails?.idType ? CustomerIdTypeEnum[initialCustomerDetails?.idType] as keyof typeof CustomerIdTypeEnum : '',
        idNo: initialCustomerDetails?.idNo || '',
        contactNo: initialCustomerDetails?.contactNo || '',
        branchName: getUserBranchName(initialCustomerDetails?.branchId) || '',
        branchCode: initialCustomerDetails?.branchCode || '',
        // gender: initialCustomerDetails?.gender ? CustomerGenderEnum[initialCustomerDetails.gender] as keyof typeof CustomerGenderEnum : '',
        nationality: initialCustomerDetails?.nationality || '',
        occupation: initialCustomerDetails?.occupation || '',
        addressLine1: initialCustomerDetails?.addressLine1 || '',
        addressLine2: initialCustomerDetails?.addressLine2 || '',
        city: initialCustomerDetails?.city || '',
        state: initialCustomerDetails?.state || '',
        postCode: initialCustomerDetails?.postCode || '',
        race: initialCustomerDetails?.race ? CustomerRaceEnum[initialCustomerDetails.race] as keyof typeof CustomerRaceEnum : '',
        // dateOfBirth: initialCustomerDetails?.dateOfBirth || '',
        fundType: initialCustomerDetails?.fundType ? CustomerAccFundTypeEnum[initialCustomerDetails.fundType] as keyof typeof CustomerAccFundTypeEnum : '',
        refCode: initialCustomerDetails?.refCode || '',
        status: initialCustomerDetails?.status ? CustomerAccStatusEnum[initialCustomerDetails?.status] as keyof typeof CustomerAccStatusEnum : '',
        additionalUserDetails: initialCustomerDetails?.additionalUserDetails?.map(user => ({
            childId: user.childId || '',
            name: user.name || '',
            idType: user.idType
                ? (CustomerIdTypeEnum[user.idType] as keyof typeof CustomerIdTypeEnum)
                : '',
            idNo: user.idNo || '',
            nationality: user.nationality || '',
            race: user.race
                ? (CustomerRaceEnum[user.race] as keyof typeof CustomerRaceEnum)
                : '',
        })) || [],
    };

    const formik = useFormik({
        initialValues: CustomerManagementCustomerDetailsFormInitialValues,
        validationSchema: CustomerManagementCustomerDetailsFormValidation.concat(CustomerManagementJointAccountValidation),
        onSubmit: (values) => {
            const castValues = CustomerManagementCustomerDetailsFormValidation.concat(CustomerManagementJointAccountValidation).cast(values);
            const {
                idType,
                //  gender,
                race,
                fundType,
                status,
                additionalUserDetails,
                refCode,
                ...restOfValues
            } = castValues;

            const additionalUserDetailsMap: TAdditionalUserDetails[] = additionalUserDetails
                ? additionalUserDetails.map((item) => {
                    return {
                        childId: item.childId,
                        idNo: item.idNo as string,
                        name: item.name as string,
                        nationality: item.nationality as string,
                        idType: CustomerIdTypeEnum[item.idType as unknown as keyof typeof CustomerIdTypeEnum] || CustomerIdTypeEnum.IC,
                        race: CustomerRaceEnum[item.race as unknown as keyof typeof CustomerRaceEnum] || CustomerRaceEnum.Malay,
                    };
                })
                : [];

            onSubmit({
                idType: CustomerIdTypeEnum[idType as keyof typeof CustomerIdTypeEnum] || CustomerIdTypeEnum.IC,
                // gender: CustomerGenderEnum[gender as keyof typeof CustomerGenderEnum] || CustomerGenderEnum.Male,
                race: CustomerRaceEnum[race as keyof typeof CustomerRaceEnum] || CustomerRaceEnum.Malay,
                fundType: CustomerAccFundTypeEnum[fundType as keyof typeof CustomerAccFundTypeEnum] || CustomerAccFundTypeEnum.Islamic,
                status: CustomerAccStatusEnum[status as keyof typeof CustomerAccStatusEnum] || CustomerAccStatusEnum.Active,
                additionalUserDetails: additionalUserDetailsMap,
                refCode: refCode ? sanitizeReferralCode(refCode) : undefined,
                ...restOfValues,
            });
        },
    });

    const shouldDisableInput = formik.values.status === CustomerAccStatusEnum[CustomerAccStatusEnum.Deactivated];

    useEffect(() => {
        // GetCountries().then(result => {
        //     const massagedResult = [
        //         result.find(item => item.name === 'Malaysia'),
        //         ...result,
        //     ].filter(item => item);

        //     setCountries(massagedResult as Country[]);
        // });

        const massagedResult = [
            countryList.find(item => item.name === 'Malaysia'),
            ...countryList,
        ].filter(item => item);

        setCountries(massagedResult as Country[]);
    }, []);

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branches.find(branch => branch.branchName === formik.values.branchName);

        if (newBranch) {
            formik.setFieldValue('branchCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchCode', '');
        }
    }, [formik.values.branchName]);

    useEffect(() => {
        if (!isEditing) {
            formik.resetForm();
        }
    }, [isEditing]);

    useEffect(() => {
        if (formik.values.idType === 'IC' && formik.values.idNo) {
            const idYear = Number(formik.values.idNo.slice(0, 2));
            const idMonth = Number(formik.values.idNo.slice(2, 4));
            const idDay = Number(formik.values.idNo.slice(4, 6));
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.round(currentYear / 1000) * 1000;
            const lastCentury = currentCentury - 100;

            // if the year provided is greater than current year (2 digit year), the year is most likely from the last century
            const inferredYear = idYear < Number(currentYear.toString().substring(2)) ? currentCentury + idYear : lastCentury + idYear;
            const inferredDate = new Date(inferredYear, idMonth - 1, idDay);

            if (dayjs(inferredDate) < dayjs().subtract(18, 'years')) {
                formik.setFieldValue('dateOfBirth', inferredDate);
            } else formik.setFieldValue('dateOfBirth', '');
        }
    }, [formik.values.idNo, formik.values.idType]);

    const idTypeOptions = Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const branchOptions = branches.filter(branch => branch.status === BranchStatusEnum.Active).map(branch => {
        return {
            label: branch.branchName,
            value: branch.branchName,
        };
    });

    const genderOptions = Object.values(CustomerGenderEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const nationalityOptions = Object.values(countries).map(country => {
        const countryName = country.name;
        return {
            label: countryName,
            value: countryName,
        };
    });

    const statusOptions = Object.values(CustomerAccStatusEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const raceOptions = Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    // conventional type is hidden for the time being
    const fundTypeOptions = Object.values(CustomerAccFundTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    // to hide conventional use this code instead
    // const fundTypeOptions = [{
    //     label: CustomerAccFundTypeEnum[CustomerAccFundTypeEnum.Islamic],
    //     value: CustomerAccFundTypeEnum[CustomerAccFundTypeEnum.Islamic],
    // }];

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Reset the form if the status is changed to not deactivated and changed back to deactivated (not allowed to update details if status is deactivated)
        if (e.target.value === CustomerAccStatusEnum[CustomerAccStatusEnum.Deactivated]
            && initialCustomerDetails?.status === CustomerAccStatusEnum.Deactivated
            && formik.values.status !== CustomerAccStatusEnum[CustomerAccStatusEnum.Deactivated]
        ) {
            toast.info('Form is reset');
            formik.resetForm();
        }

        formik.setFieldValue('status', e.target.value);
    };

    const renderAdditionalUserDetails = () => {
        return (
            <div className={FormStyles['form-container']}>
                {formik.values.additionalUserDetails?.map((userDetails, index) => {
                    return (
                        <React.Fragment key={index}>
                            <LabelledInput
                                id='name'
                                name={`additionalUserDetails[${index}].name`}
                                label='Child Name* :'
                                value={formik.values.additionalUserDetails?.[index].name || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].name ? formik.errors.additionalUserDetails?.[index].name : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={FormStyles['span-2']}
                                disabled={inputIsDisabled || shouldDisableInput}
                            />

                            <LabelledDropdown
                                id='idType'
                                name={`additionalUserDetails[${index}].idType`}
                                label='ID Type* :'
                                options={idTypeOptions}
                                value={formik.values.additionalUserDetails?.[index].idType || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].idType ? formik.errors.additionalUserDetails?.[index].idType : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].idType`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                disabled={inputIsDisabled || shouldDisableInput}
                                noOptionsMessage='No results found'
                                optionSelected={!!CustomerIdTypeEnum[formik.values.idType as keyof typeof CustomerIdTypeEnum]}
                            />

                            <LabelledInput
                                id='idNo'
                                name={`additionalUserDetails[${index}].idNo`}
                                label='ID No.* :'
                                value={formik.values.additionalUserDetails?.[index].idNo || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].idNo ? formik.errors.additionalUserDetails?.[index].idNo : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={inputIsDisabled || shouldDisableInput}
                            />

                            <LabelledDropdown
                                id='nationality'
                                name={`additionalUserDetails[${index}].nationality`}
                                label='Nationality* :'
                                options={nationalityOptions}
                                value={formik.values.additionalUserDetails?.[index].nationality || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].nationality ? formik.errors.additionalUserDetails?.[index].nationality : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].nationality`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                noOptionsMessage='No country found'
                                disabled={inputIsDisabled || shouldDisableInput}
                                optionSelected={!!nationalityOptions.find(option => option.value === formik.values.nationality)}
                            />

                            <LabelledDropdown
                                id='race'
                                name={`additionalUserDetails[${index}].race`}
                                label='Race* :'
                                options={raceOptions}
                                value={formik.values.additionalUserDetails?.[index].race || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].race ? formik.errors.additionalUserDetails?.[index].race : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].race`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                disabled={inputIsDisabled || shouldDisableInput}
                                noOptionsMessage='No race found'
                                optionSelected={!!raceOptions.find(option => option.value === formik.values.race)}
                            />
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='customerId'
                    name='customerId'
                    label='Customer ID* :'
                    value={initialCustomerDetails?.customerId.toString()}
                    disabled={true}
                />

                <LabelledInput
                    id='name'
                    name='name'
                    label='Customer Name* :'
                    value={formik.values.name}
                    error={formik.touched.name ? formik.errors.name : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='email'
                    name='email'
                    label='Email :'
                    value={formik.values.email}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='CIFNo'
                    name='CIFNo'
                    label='CIF No.* :'
                    value={formik.values.CIFNo}
                    error={formik.touched.CIFNo ? formik.errors.CIFNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput || !isSuperAdmin}
                />

                <LabelledDropdown
                    id='idType'
                    name='idType'
                    label='ID Type* :'
                    options={idTypeOptions}
                    value={formik.values.idType || ''}
                    error={formik.touched.idType ? formik.errors.idType : undefined}
                    onChange={(e) => formik.setFieldValue('idType', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled || shouldDisableInput || !isSuperAdmin}
                    noOptionsMessage='No results found'
                    optionSelected={!!CustomerIdTypeEnum[formik.values.idType as keyof typeof CustomerIdTypeEnum]}
                />

                <LabelledInput
                    id='idNo'
                    name='idNo'
                    label='ID No.* :'
                    value={formik.values.idNo}
                    error={formik.touched.idNo ? formik.errors.idNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput || !isSuperAdmin}
                />

                <LabelledInput
                    id='contactNo'
                    name='contactNo'
                    label='Contact No.* :'
                    value={formik.values.contactNo}
                    error={formik.touched.contactNo ? formik.errors.contactNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledDropdown
                    id='branchName'
                    name='branchName'
                    label='Branch Name* :'
                    options={branchOptions}
                    value={formik.values.branchName || ''}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={(e) => formik.setFieldValue('branchName', e.target.value)}
                    autoComplete='off'
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                    optionSelected={!!branchOptions.find(option => option.value === formik.values.branchName)}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode || ''}
                    disabled={true}
                />

                <LabelledDropdown
                    id='nationality'
                    name='nationality'
                    label='Nationality* :'
                    options={nationalityOptions}
                    value={formik.values.nationality}
                    error={formik.touched.nationality ? formik.errors.nationality : undefined}
                    onChange={(e) => formik.setFieldValue('nationality', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    noOptionsMessage='No country found'
                    disabled={inputIsDisabled || shouldDisableInput}
                    optionSelected={!!nationalityOptions.find(option => option.value === formik.values.nationality)}
                />

                <LabelledInput
                    id='occupation'
                    name='occupation'
                    label='Occupation* :'
                    value={formik.values.occupation}
                    error={formik.touched.occupation ? formik.errors.occupation : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledDropdown
                    id='race'
                    name='race'
                    label='Race* :'
                    options={raceOptions}
                    value={formik.values.race || ''}
                    error={formik.touched.race ? formik.errors.race : undefined}
                    onChange={(e) => formik.setFieldValue('race', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled || shouldDisableInput}
                    noOptionsMessage='No race found'
                    optionSelected={!!raceOptions.find(option => option.value === formik.values.race)}
                />

                <LabelledDropdown
                    id='status'
                    name='status'
                    label='Account Status* :'
                    value={formik.values.status}
                    error={formik.touched.status ? formik.errors.status : undefined}
                    onChange={handleStatusChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                    options={statusOptions}
                    autoComplete='off'
                    optionSelected={!!statusOptions.find(option => option.value === formik.values.status)}
                />

                <LabelledInput
                    data-testid='customer-details-paperGoldBalance-input'
                    id='paperGoldBalance'
                    name='paperGoldBalance'
                    label='Paper Gold Balance* (g):'
                    value={initialCustomerDetails?.paperGoldBalance}
                    disabled={true}
                />

                <LabelledInput
                    id='addressLine1'
                    name='addressLine1'
                    label='Address Line 1* :'
                    value={formik.values.addressLine1}
                    error={formik.touched.addressLine1 ? formik.errors.addressLine1 : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='paperSilverBalance'
                    name='paperSilverBalance'
                    label='Paper Silver Balance* (g):'
                    value={initialCustomerDetails?.paperSilverBalance}
                    disabled={true}
                />

                <LabelledInput
                    id='addressLine2'
                    name='addressLine2'
                    label='Address Line 2 :'
                    value={formik.values.addressLine2}
                    error={formik.touched.addressLine2 ? formik.errors.addressLine2 : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='city'
                    name='city'
                    label='City* :'
                    value={formik.values.city}
                    error={formik.touched.city ? formik.errors.city : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='state'
                    name='state'
                    label='State* :'
                    value={formik.values.state}
                    error={formik.touched.state ? formik.errors.state : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='postCode'
                    name='postCode'
                    label='Postcode* :'
                    value={formik.values.postCode}
                    error={formik.touched.postCode ? formik.errors.postCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledDropdown
                    id='fundType'
                    name='fundType'
                    label='Fund Type* :'
                    options={fundTypeOptions}
                    value={formik.values.fundType || ''}
                    error={formik.touched.fundType ? formik.errors.fundType : undefined}
                    onChange={(e) => formik.setFieldValue('fundType', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled || shouldDisableInput}
                    noOptionsMessage='No fund type found'
                    optionSelected={!!FundTypeEnum[formik.values.fundType as keyof typeof FundTypeEnum]}
                />

                <LabelledInput
                    id='refCode'
                    name='refCode'
                    label='Referral Code :'
                    value={formik.values.refCode}
                    error={formik.touched.refCode ? formik.errors.refCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledInput
                    id='createdAt'
                    name='createdAt'
                    label='Account Created At :'
                    value={dateTimeFormat(initialCustomerDetails?.createdAt || '')}
                    disabled={true}
                />

                <LabelledInput
                    id='updatedAt'
                    name='updatedAt'
                    label='Account Last Updated At :'
                    value={dateTimeFormat(initialCustomerDetails?.updatedAt || '')}
                    disabled={true}
                />

                <LabelledInput
                    id='goldEarmarkEndDate'
                    name='goldEarmarkEndDate'
                    label='Gold Earmark End Date* :'
                    data-testid='customer-earmarkEndDate-input'
                    value={
                        initialCustomerDetails?.goldEarmarkEndDate
                            ? dateTimeFormat(initialCustomerDetails.goldEarmarkEndDate)
                            : 'N/A'
                    }
                    disabled={true}
                />

                <LabelledInput
                    id='goldEarmarkQuantity'
                    name='goldEarmarkQuantity'
                    label='Gold Earmark Quantity* :'
                    value={initialCustomerDetails?.goldEarmarkBalance}
                    disabled={true}
                />

                <LabelledInput
                    id='silverEarmarkEndDate'
                    name='silverEarmarkEndDate'
                    label='Silver Earmark End Date* :'
                    value={
                        initialCustomerDetails?.silverEarmarkEndDate
                            ? dateTimeFormat(initialCustomerDetails.silverEarmarkEndDate)
                            : 'N/A'
                    }
                    disabled={true}
                />

                <LabelledInput
                    id='silverEarmarkQuantity'
                    name='silverEarmarkQuantity'
                    label='Silver Earmark Quantity* :'
                    value={initialCustomerDetails?.silverEarmarkBalance}
                    data-testid='customer-earmarkQuantity-input'
                    disabled={true}
                />

                {initialCustomerDetails?.accountType === CustomerAccTypeEnum.Joint && (
                    <div className={`${FormStyles['joint-account-details-container']}  ${FormStyles['span-2']}`}>
                        <Text
                            isBold
                            label={'Child Details'}
                        />
                        {renderAdditionalUserDetails()}
                    </div>
                )}

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default CustomerManagementCustomerDetailsForm;
